<script lang="ts">
export interface PaginationBarProps {
	pageSizeOptions?: Array<number>;
	/**
	 * Total items count.
	 *
	 * Provide only if the total number of items is known.
	 */
	totalItems?: number;
	/**
	 * The current maximum number of known items.
	 */
	currentItems?: number;
	/**
	 * How many of the counted items live outside the paged result, such as unsaved rows a relation
	 * field appends to page one.
	 *
	 * They are still counted in `totalItems` / `currentItems`, but they never add a page: without
	 * this the bar offers a page the underlying query cannot serve.
	 */
	unpagedItems?: number;
	loading?: boolean;
}
</script>

<script setup lang="ts">
import { useElementSize } from '@vueuse/core';
import { computed, ref, toValue, useTemplateRef, watchEffect } from 'vue';
import { useI18n } from 'vue-i18n';
import { isNil, isNullishOrEmpty } from '#layers/base/public';

const props = withDefaults(defineProps<PaginationBarProps>(), {
	pageSizeOptions: () => [25, 100, 250, 1000],
});
const PAGINATION_INFO_BREAKPOINT = 720;
const PAGINATION_MINIMAL_BREAKPOINT = 576;

const page = defineModel<number>('page', {
	default: 1,
});

const pageSize = defineModel<number>('pageSize', {
	default: 25,
});

const { t } = useI18n();

const paginationBar = useTemplateRef<HTMLDivElement>('pagination-bar');

const {
	isPaginationInfoHidden,
	isPaginationMinimal,
	isPaginationHidden,
	isPageSizeHidden,
} = usePaginationVisibility();

const perPageOptions = computed(() =>
	props.pageSizeOptions.map((value) => ({
		label: t('common.per_page', { items: value }),
		value,
	})),
);

const totalOrCurrentCount = computed(() => {
	if (!isNullishOrEmpty(props.totalItems)) {
		return props.totalItems;
	}

	return props.currentItems ?? 0;
});

/** How many items the pages actually hold, which is what the page controls are derived from. */
const pageableCount = computed(() => Math.max(0, totalOrCurrentCount.value - (props.unpagedItems ?? 0)));

/** Last page the underlying query can serve; `undefined` while the total is unknown. */
const lastPage = computed(() => {
	if (isNullishOrEmpty(props.totalItems)) return;
	return Math.max(1, Math.ceil(pageableCount.value / pageSize.value));
});

const paginationInfo = ref('');

watchEffect(() => {
	// Delay updating pagination info while loading to prevent flickering
	if (props.loading) {
		return;
	}

	// Will track reactivity deps inside getPaginationInfo
	paginationInfo.value = getPaginationInfo();
});

function getPaginationInfo() {
	const pageValue = toValue(page);
	const pageSizeValue = toValue(pageSize);
	const totalItemsValue = props.totalItems;
	const currentItemsValue = props.currentItems;

	if (isNil(pageValue) || isNil(pageSizeValue)) {
		return '';
	}

	const start = ((pageValue - 1) * pageSizeValue) + 1;
	const end = Math.min(pageValue * pageSizeValue, totalOrCurrentCount.value);

	if (isNil(totalItemsValue)) {
		if (!isNil(currentItemsValue)) {
			if (currentItemsValue === 0) {
				return t('common.no_items');
			}

			return t('common.items_range_no_total', {
				start,
				end,
			});
		}
		else {
			return '';
		}
	}

	if (totalItemsValue === 0) {
		return t('common.no_items');
	}

	if (pageValue === 1 && end === totalItemsValue) {
		return t('common.n_items', totalItemsValue);
	}

	return t('common.items_range', { start, end, total: totalItemsValue });
}

function usePaginationVisibility() {
	const { width: paginationBarWidth } = useElementSize(paginationBar, { width: PAGINATION_INFO_BREAKPOINT, height: 0 });
	const isPaginationInfoHidden = computed(() => paginationBarWidth.value < PAGINATION_INFO_BREAKPOINT);
	const isPaginationMinimal = computed(() => paginationBarWidth.value < PAGINATION_MINIMAL_BREAKPOINT);
	const isPaginationHidden = computed(() => !isNil(props.totalItems) && pageableCount.value <= pageSize.value);
	const isPageSizeHidden = computed(() => props.pageSizeOptions.length === 0 || (!isNil(props.totalItems) && props.totalItems <= props.pageSizeOptions[0]!));

	return {
		isPaginationInfoHidden,
		isPaginationMinimal,
		isPaginationHidden,
		isPageSizeHidden,
	};
}
</script>

<template>
	<div
		ref="pagination-bar"
		class="flex shrink-0 items-center justify-between gap-x-2 overflow-x-auto px-5 block-(--footer-height)"
		data-testid="pagination-bar"
	>
		<div class="flex h-8 shrink-0 gap-1">
			<template v-if="!isPaginationHidden">
				<DButton
					data-testid="pagination-previous"
					icon="lucide:chevron-left"
					color="neutral"
					variant="ghost"
					:disabled="page <= 1"
					:aria-label="t('pagination.previous_page')"
					@click="page--"
				/>

				<DPagination
					v-model:page="page"
					:items-per-page="pageSize"
					:total="pageableCount"
					:sibling-count="isPaginationMinimal ? 0 : 1"
					:show-edges="!isPaginationMinimal"
					:show-controls="false"
				/>

				<DButton
					data-testid="pagination-next"
					icon="lucide:chevron-right"
					color="neutral"
					variant="ghost"
					:disabled="lastPage !== undefined && page >= lastPage"
					:aria-label="t('pagination.next_page')"
					@click="page++"
				/>
			</template>
		</div>

		<div class="flex shrink items-center gap-4 min-inline-0">
			<div v-if="!isPaginationInfoHidden" class="truncate text-sm text-muted" data-testid="pagination-info">
				{{ paginationInfo }}
			</div>
			<div
				v-if="!isPageSizeHidden"
				class="shrink-0"
			>
				<DSelect
					v-model="pageSize"
					:items="perPageOptions"
					size="sm"
					:aria-label="t('pagination.items_per_page')"
				/>
			</div>
		</div>
	</div>
</template>
